import React, { useState, useEffect } from 'react'
import { Box, TextField, Typography } from '@mui/material'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { validateVanityUrlAtom } from '../../api/vanityUrls.atoms'
import Configs from 'infrastructure/config/environment'

const VALID_CHARS_REGEX = /^[A-Za-z0-9\-_.~]*$/

interface Props {
  /** The current value of the vanity URL input */
  value: string
  /** Callback function that is called when the input value changes */
  onChange: (value: string, isValid: boolean) => void
  /** Whether the current value is valid */
  isValid: boolean
  /** The original value of the vanity URL (for comparison) */
  originalValue?: string
  /** The prompt text to display above the input */
  prompt: string
}

/**
 * VanityUrlInput is a reusable component that provides a text input field for vanity URLs
 * with built-in validation and error messaging.
 *
 * @param {string} value - The current value of the vanity URL input
 * @param {(value: string, isValid: boolean) => void} onChange - Callback function that is called when the input value changes
 * @param {boolean} isValid - Whether the current value is valid
 * @param {string} [originalValue=''] - The original value of the vanity URL (for comparison)
 * @param {string} prompt - The prompt text to display above the input
 *
 * @returns {JSX.Element} The rendered vanity URL input component
 */
export function VanityUrlInput({
  value,
  onChange,
  isValid,
  originalValue = '',
  prompt,
}: Props) {
  const validateUrl = useAtomValue(validateVanityUrlAtom)

  const handleVanityUrlChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = e.target.value.toUpperCase()
    if (VALID_CHARS_REGEX.test(newValue)) {
      const isValidUrl = await validateUrl(newValue)
      // If the new value matches the original value, it's valid
      if (originalValue && newValue === originalValue) {
        onChange(newValue, true)
        return
      }
      onChange(newValue, isValidUrl)
    }
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography>{prompt}</Typography>
        {value === '' ? (
          <Typography color="error" variant="caption">
            URL cannot be empty
          </Typography>
        ) : !isValid ? (
          <Typography color="error" variant="caption">
            URL in use
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {`${Configs().AppUrl}/curriculum/${value}`}
          </Typography>
        )}
      </Box>
      <TextField
        fullWidth
        size="small"
        value={value}
        onChange={handleVanityUrlChange}
        placeholder="Enter vanity CUID"
      />
    </Box>
  )
}
